import { create } from 'zustand';
import { User } from '../types/user';
import { fetchMe, loginJson } from '../services/authService';
import { getActiveDepartments } from '../services/departmentService';
import { setStoredToken, setStoredUser, getStoredUser, getStoredToken } from '../services/api';
import { queryClient } from '../services/queryClient';

const DEPT_KEY = 'wiki-active-dept';
const DEPT_NAME_KEY = 'wiki-active-dept-name';
const VIEW_ALL_KEY = 'wiki-view-all-depts';

function getStoredDept(): string | null {
  return localStorage.getItem(DEPT_KEY) || null;
}

function saveDept(slug: string | null) {
  if (slug) localStorage.setItem(DEPT_KEY, slug);
  else localStorage.removeItem(DEPT_KEY);
}

function getStoredDeptName(): string | null {
  return localStorage.getItem(DEPT_NAME_KEY) || null;
}

function saveDeptName(name: string | null) {
  if (name) localStorage.setItem(DEPT_NAME_KEY, name);
  else localStorage.removeItem(DEPT_NAME_KEY);
}

function apiUserToUser(data: { username: string; roles: string[]; departments: string[]; full_name?: string }): User {
  const rawRoles = data.roles;
  const roles = Array.isArray(rawRoles) ? rawRoles : (typeof rawRoles === 'string' ? [rawRoles] : []);
  return {
    id: 0,
    username: data.username,
    full_name: data.full_name,
    roles,
    departments: data.departments || [],
    created_at: null,
    documents: null,
  };
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  activeDepartment: string | null;
  activeDepartmentName: string | null;
  login: (username: string, password: string) => Promise<void>;
  fetchUser: () => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isSystemAdmin: () => boolean;
  canManageDoc: (doc?: { department?: { slug: string } | null } | null) => boolean;
  setActiveDepartment: (slug: string | null) => void;
  userDepartments: () => string[];
  departmentCount: () => number;
  viewAllDepartments: boolean;
  setViewAllDepartments: (value: boolean) => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: getStoredUser<User>(),
  loading: true,
  activeDepartment: getStoredDept(),
  activeDepartmentName: getStoredDeptName(),
  viewAllDepartments: localStorage.getItem(VIEW_ALL_KEY) === 'true',

  login: async (username: string, password: string) => {
    const data = await loginJson(username, password);
    setStoredToken(data.token);
    setStoredUser(data.user);
    const user = apiUserToUser(data.user);
    let activeDept = getStoredDept();
    const userDepts = data.user.departments || [];
    if (!activeDept) {
      activeDept = userDepts.length === 1 ? userDepts[0]! : null;
    } else if (!userDepts.includes(activeDept)) {
      activeDept = userDepts.length === 1 ? userDepts[0]! : null;
      saveDept(activeDept);
    }
    set({ user, loading: false, activeDepartment: activeDept });

    // Re-fetch desde /me para sincronizar claims (ej: el backend aplica
    // fallback member_of -> departments que el SSO no emite). Sin esto,
    // el frontend puede quedar con departments=[] hasta el proximo refresh.
    try {
      const fresh = await fetchMe();
      const freshDepts = fresh.departments || [];
      const freshUser = apiUserToUser(fresh);
      if (!activeDept) {
        activeDept = freshDepts.length === 1 ? freshDepts[0]! : null;
      } else if (!freshDepts.includes(activeDept)) {
        activeDept = freshDepts.length === 1 ? freshDepts[0]! : null;
        saveDept(activeDept);
      }
      setStoredUser(freshUser);
      set({ user: freshUser, activeDepartment: activeDept });
      // Cachear el nombre del depto activo para que el Header lo muestre
      // inmediatamente (sin esperar al fetch de /documents/departments/active).
      await _resolveDeptName(activeDept, freshDepts);
    } catch {
      // Si falla, el state actual (con departments del SSO) se mantiene
    }
  },

  fetchUser: async () => {
    if (!getStoredToken()) {
      set({ user: null, loading: false, activeDepartment: null, activeDepartmentName: null });
      return;
    }
    try {
      const data = await fetchMe();
      const userDepts = data.departments || [];
      const user = apiUserToUser(data);
      let activeDept = getStoredDept();
      if (!activeDept) {
        activeDept = userDepts.length === 1 ? userDepts[0]! : null;
      } else if (!userDepts.includes(activeDept)) {
        activeDept = userDepts.length === 1 ? userDepts[0]! : null;
        saveDept(activeDept);
      }
      set({ user, loading: false, activeDepartment: activeDept });
      await _resolveDeptName(activeDept, userDepts);
    } catch {
      setStoredToken(null);
      setStoredUser(null);
      set({ user: null, loading: false, activeDepartment: null, activeDepartmentName: null });
    }
  },

  refreshUser: async () => {
    // Re-fetch del user desde el backend. Util cuando el localStorage tiene
    // un user viejo (ej: SSO cambio claims y hay nuevos departments).
    // Noop si no hay token.
    if (!getStoredToken()) return;
    try {
      const data = await fetchMe();
      const userDepts = data.departments || [];
      const user = apiUserToUser(data);
      let activeDept = getStoredDept();
      if (!activeDept) {
        activeDept = userDepts.length === 1 ? userDepts[0]! : null;
      } else if (!userDepts.includes(activeDept)) {
        activeDept = userDepts.length === 1 ? userDepts[0]! : null;
        saveDept(activeDept);
      }
      setStoredUser(user);
      set({ user, loading: false, activeDepartment: activeDept });
      await _resolveDeptName(activeDept, userDepts);
    } catch {
      // Si falla (401, 403, red), no hacemos nada — el user actual se mantiene
    }
  },

  logout: () => {
    queryClient.clear();
    setStoredToken(null);
    setStoredUser(null);
    localStorage.removeItem(DEPT_KEY);
    localStorage.removeItem(DEPT_NAME_KEY);
    localStorage.removeItem(VIEW_ALL_KEY);
    set({ user: null, loading: false, activeDepartment: null, activeDepartmentName: null, viewAllDepartments: false });
    window.location.href = '/login';
  },

  hasRole: (role: string) => {
    const { user } = get();
    const roles = user?.roles || [];
    return roles.map(r => r.toUpperCase()).includes(role.toUpperCase());
  },

  isAdmin: () => {
    const { user } = get();
    const roles = (user?.roles || []).map(r => r.toUpperCase());
    return roles.includes('ROOT') || roles.includes('ADMIN') || roles.includes('IT_ADMIN');
  },

  isSystemAdmin: () => {
    const { user } = get();
    const roles = (user?.roles || []).map(r => r.toUpperCase());
    return roles.includes('IT_ADMIN');
  },

  // Scope departamental: el rol da la capacidad, el departamento el alcance.
  // IT_ADMIN ve todo; un doc sin departamento solo lo maneja IT_ADMIN.
  canManageDoc: (doc) => {
    if (get().isSystemAdmin()) return true;
    const docSlug = doc?.department?.slug;
    return !!docSlug && get().userDepartments().includes(docSlug);
  },

  setActiveDepartment: (slug: string | null) => {
    saveDept(slug);
    set({ activeDepartment: slug, activeDepartmentName: getStoredDeptName() });
    const currentDepts = get().user?.departments || [];
    void _resolveDeptName(slug, currentDepts);
  },

  userDepartments: () => {
    return get().user?.departments || [];
  },

  departmentCount: () => {
    return get().user?.departments?.length || 0;
  },

  setViewAllDepartments: (value: boolean) => {
    if (value) localStorage.setItem(VIEW_ALL_KEY, 'true');
    else localStorage.removeItem(VIEW_ALL_KEY);
    set({ viewAllDepartments: value });
    void queryClient.invalidateQueries({ queryKey: ['documents'] });
    void queryClient.invalidateQueries({ queryKey: ['search'] });
  },
}));

/** Cachea el nombre del depto activo en el store + localStorage.
 *  Se llama cada vez que cambia activeDepartment. El Header puede
 *  mostrar el nombre inmediatamente (sin esperar al fetch). */
async function _resolveDeptName(slug: string | null, userDeptSlugs: string[]): Promise<void> {
  if (!slug) {
    saveDeptName(null);
    useAuth.setState({ activeDepartmentName: null });
    return;
  }
  // Si el slug no esta entre los deptos del user, invalida.
  if (userDeptSlugs.length > 0 && !userDeptSlugs.includes(slug)) {
    saveDeptName(null);
    useAuth.setState({ activeDepartmentName: null });
    return;
  }
  useAuth.setState({ activeDepartmentName: getStoredDeptName() });
  // Fetch para refrescar (por si el depto fue renombrado en el AD).
  try {
    const all = await getActiveDepartments();
    const match = all.find((d: { slug: string }) => d.slug === slug);
    if (match) {
      saveDeptName(match.name);
      useAuth.setState({ activeDepartmentName: match.name });
    }
  } catch {
    // Si falla el fetch, conservamos el cache existente.
  }
}
